/**
 * Tenant resolution.
 *
 * Today: one tenant per Clerk user (org_id = clerk user id).
 * Tomorrow: when real Clerk Organizations are introduced, swap to org id.
 *
 * Always go through these helpers — never read clerk user id directly into
 * an `org_id` column elsewhere in the codebase.
 */

import { useAuth, useUser } from '@clerk/clerk-react'
import { activeOrgId } from './activeOrg'

/**
 * The tenant media belongs to: the ACTIVE ORGANISATION when organisations are on
 * (Step 2, 30 Sep 2026 — image, video and PDF libraries follow it), otherwise
 * the user's own id, exactly as before. activeOrgId() is never set in
 * production, so production is unchanged.
 */
function tenantOf(userId: string | null | undefined): string | null {
  return activeOrgId() ?? userId ?? null
}

/** Hook for components: returns current tenant id (or null while loading) */
export function useTenantId(): string | null {
  const { userId, isLoaded } = useAuth()
  if (!isLoaded) return null
  return tenantOf(userId)
}

/** Hook returning both the tenant id and current Clerk user id */
export function useTenantAndUser(): { orgId: string | null; userId: string | null } {
  const { userId, isLoaded } = useAuth()
  if (!isLoaded) return { orgId: null, userId: null }
  return { orgId: tenantOf(userId), userId: userId ?? null }
}

/** For places that need the user's display info alongside the tenant id */
export function useTenantWithUser() {
  const { userId, isLoaded: authLoaded } = useAuth()
  const { user, isLoaded: userLoaded } = useUser()
  return {
    orgId: tenantOf(userId),
    userId: userId ?? null,
    email: user?.primaryEmailAddress?.emailAddress ?? null,
    name: user?.fullName ?? null,
    isLoaded: authLoaded && userLoaded,
  }
}

/**
 * Server-side resolver — used in /api routes to derive org_id from a Clerk
 * session token in the request. Today the org_id IS the user id, so this is a
 * thin pass-through. When real orgs land, this is the single place to change.
 */
export function resolveOrgIdFromUserId(clerkUserId: string): string {
  return clerkUserId
}

// The active organisation lives in ./activeOrg (no React, no Clerk) so the data
// layer can read it without pulling Clerk in; re-exported here as the tenancy entry point.
export { activeOrgId, activeOrgIsOwned, mediaOwnerIds, setActiveOrgId } from './activeOrg'
